import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useClient, useLoading, useQuery } from '@based/sdk/react'
import { pages } from '@app/util/links'
import { useTranslations } from '@i18n/useLanguage'
import { useInfiniteScroll } from '@app/util/useInfiniteScroll'
import { PageLayout } from '../layout/PageLayout'
import { Title } from '@app/components/core/typography/Title'
import { StaticPageSeo } from '@app/components/core/seo/StaticPageSeo'
import { EventCard } from '@app/components/blocks/event/EventCard'
import Column from '@app/components/layout/Column'
import { Empty } from '@app/components/blocks/empty/Empty'
import { ListEventModel } from 'functions/lib/event/eventList'
import defaultProjectPicture from '@app/assets/defaultProjectPicture.png'

const PAGE_SIZE = 10

export const Events = () => {
  const t = useTranslations().app.eventsPage
  return (
    <PageLayout>
      <StaticPageSeo
        path={pages.events}
        pageTitle={t.title}
        description={t.description}
      />
      <Title.H1 style={{ textAlign: 'center' }}>{t.title}</Title.H1>
      <EventsList />
    </PageLayout>
  )
}

const EventsList = () => {
  const t = useTranslations().app
  const [events, setEvents] = useState<ListEventModel[]>([])
  const [top, setTop] = useState(PAGE_SIZE)
  const [mounted, setMounted] = useState(false)
  const client = useClient()

  const dataServerQuery = useQuery('list-platform-events', {
    top: PAGE_SIZE,
    filterPastEvents: false,
  })
  const serverEvents: ListEventModel[] = dataServerQuery.data ?? []

  const isLoading = useLoading()
  const serverHasMore = serverEvents.length === PAGE_SIZE
  const [hasMore, setHasMore] = useState(serverHasMore)
  const isInitialClientLoad = useRef(true)
  const prevLengthRef = useRef(serverEvents.length)

  const query = useCallback(
    (topParam: number) => {
      const close = client
        .query('list-platform-events', {
          top: topParam,
          filterPastEvents: false,
        })
        .subscribe((result) => {
          setEvents(result ?? [])
          setMounted(true)
        })
      return () => close()
    },
    [client],
  )

  useEffect(() => {
    setTop(PAGE_SIZE)
    setMounted(false)
    isInitialClientLoad.current = true
    prevLengthRef.current = serverEvents.length
    const cleanup = query(PAGE_SIZE)
    return cleanup
  }, [query])

  useEffect(() => {
    if (top === PAGE_SIZE) return
    const cleanup = query(top)
    return cleanup
  }, [top, query])

  const renderedEvents = mounted ? events : serverEvents

  useEffect(() => {
    if (!mounted) return
    if (isInitialClientLoad.current) {
      isInitialClientLoad.current = false
      prevLengthRef.current = events.length
      return
    }
    const growth = events.length - prevLengthRef.current
    prevLengthRef.current = events.length
    if (growth < PAGE_SIZE) {
      setHasMore(false)
    }
  }, [events.length, mounted])

  const loadMore = useCallback(() => setTop((prev) => prev + PAGE_SIZE), [])

  const { lastItemRef } = useInfiniteScroll({
    isLoading,
    hasMore,
    loadMore,
    itemCount: renderedEvents.length,
  })

  const now = Date.now()
  const startOfToday = new Date(now)
  startOfToday.setHours(0, 0, 0, 0)

  const isPastEvent = (event: ListEventModel) =>
    event.start < startOfToday.getTime() && event.end !== 0 && event.end < now

  const [upcomingEvents, pastEvents] = useMemo(() => {
    const upcoming: ListEventModel[] = []
    const past: ListEventModel[] = []
    for (const event of renderedEvents) {
      if (isPastEvent(event)) {
        past.push(event)
      } else {
        upcoming.push(event)
      }
    }
    return [upcoming, past]
  }, [renderedEvents])

  if (isLoading && renderedEvents.length === 0) {
    return <div>{t.common.loading}</div>
  }

  if (renderedEvents.length === 0) {
    return <Empty title={t.eventPage.noContent} />
  }

  const renderCard = (item: ListEventModel, isLast: boolean) => {
    const card = (
      <EventCard
        key={item.id}
        title={item.title}
        start={item.start}
        end={item.end}
        image={item.images?.[0]?.src ?? defaultProjectPicture}
        imageAlt={item.images?.[0]?.altText}
        to={pages.event(item.slug)}
        users={item.users}
      />
    )

    if (isLast) {
      return (
        <span key={item.id} ref={lastItemRef}>
          {card}
        </span>
      )
    }

    return <span key={item.id}>{card}</span>
  }

  return (
    <Column
      style={{
        width: '100%',
        maxWidth: '50rem',
        alignSelf: 'center',
        alignItems: 'stretch',
      }}
    >
      {upcomingEvents.map((item, index) =>
        renderCard(
          item,
          index === upcomingEvents.length - 1 && pastEvents.length === 0,
        ),
      )}
      {pastEvents.length > 0 && (
        <>
          <Title.H3 style={{ marginTop: '2rem', marginBottom: 0 }}>
            {t.eventsPage.pastEvents}
          </Title.H3>
          {pastEvents.map((item, index) =>
            renderCard(item, index === pastEvents.length - 1),
          )}
        </>
      )}
    </Column>
  )
}
